import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

/**
 * Worker request/response types - must match the worker file
 */
export interface WorkerRequest {
  id: string;
  type: 'decode-proto' | 'process-ply' | 'process-geometry' | 'process-part' | 'compute-bvh';
  payload: any;
}

export interface WorkerResponse {
  id: string;
  type: 'success' | 'error' | 'progress';
  payload?: any;
  error?: string;
  progress?: number;
}

export interface ProcessedGeometry {
  positions: Float32Array;
  indices: Uint32Array;
  normals: Float32Array;
  boundingBox?: {
    min: { x: number; y: number; z: number };
    max: { x: number; y: number; z: number };
  };
  boundingSphere?: {
    center: { x: number; y: number; z: number };
    radius: number;
  };
}

export interface ProcessedPart {
  id: string;
  name: string;
  color: string;
  parentId?: string;
  faces: ProcessedGeometry[];
  faceIds: string[];
  wires: number[][];
}

export interface BVHComputeOptions {
  maxLeafSize?: number;
  maxDepth?: number;
  strategy?: number;
}

export interface BVHComputeResult {
  serializedBVH: any; // SerializedBVH from three-mesh-bvh
}

interface PendingTask {
  resolve: (value: any) => void;
  reject: (error: any) => void;
}

interface WorkerInstance {
  worker: Worker;
  busy: boolean;
  taskCount: number;
}

/**
 * Service for managing a pool of Web Workers for geometry processing.
 * Handles task distribution, worker lifecycle, and result aggregation.
 */
@Injectable({
  providedIn: 'root'
})
export class GeometryWorkerService implements OnDestroy {
  private workers: WorkerInstance[] = [];
  private pendingTasks = new Map<string, PendingTask>();
  private taskQueue: { request: WorkerRequest; resolve: (v: any) => void; reject: (e: any) => void }[] = [];
  private taskIdCounter = 0;
  private maxWorkers: number;
  private workersSupported = false;

  public isProcessing$ = new BehaviorSubject<boolean>(false);
  public progress$ = new BehaviorSubject<{ current: number; total: number }>({ current: 0, total: 0 });

  constructor() {
    // Determine optimal worker count based on CPU cores
    this.maxWorkers = Math.max(1, Math.min(navigator.hardwareConcurrency || 4, 8));
    
    // Check if workers are supported
    this.workersSupported = typeof Worker !== 'undefined';
    
    if (this.workersSupported) {
      console.log(`GeometryWorkerService: Initializing with ${this.maxWorkers} workers`);
    } else {
      console.warn('GeometryWorkerService: Web Workers not supported, falling back to main thread');
    }
  }

  /**
   * Create a new worker instance
   */
  private createWorker(): WorkerInstance {
    const worker = new Worker(
      new URL('./geometry-processing.worker', import.meta.url),
      { type: 'module' }
    );

    const instance: WorkerInstance = {
      worker,
      busy: false,
      taskCount: 0
    };

    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      this.handleWorkerMessage(instance, event.data);
    };

    worker.onerror = (error) => {
      console.error('Worker error:', error);
      // Mark worker as not busy so it can be reused or replaced
      instance.busy = false;
      this.processNextInQueue();
    };

    return instance;
  }

  /**
   * Get an available worker or create a new one
   */
  private getAvailableWorker(): WorkerInstance | null {
    // Find an idle worker
    const idleWorker = this.workers.find(w => !w.busy);
    if (idleWorker) return idleWorker;

    // Create new worker if under limit
    if (this.workers.length < this.maxWorkers) {
      const newWorker = this.createWorker();
      this.workers.push(newWorker);
      return newWorker;
    }

    return null;
  }

  /**
   * Handle message from worker
   */
  private handleWorkerMessage(instance: WorkerInstance, response: WorkerResponse) {
    instance.busy = false;
    instance.taskCount++;

    const pending = this.pendingTasks.get(response.id);
    if (pending) {
      this.pendingTasks.delete(response.id);

      if (response.type === 'success') {
        pending.resolve(response.payload);
      } else if (response.type === 'error') {
        pending.reject(new Error(response.error));
      }
    }

    // Process next task in queue
    this.processNextInQueue();
    
    // Update processing state
    if (this.pendingTasks.size === 0 && this.taskQueue.length === 0) {
      this.isProcessing$.next(false);
    }
  }

  /**
   * Process next task from queue
   */
  private processNextInQueue() {
    if (this.taskQueue.length === 0) return;

    const worker = this.getAvailableWorker();
    if (!worker) return;

    const task = this.taskQueue.shift()!;
    this.executeOnWorker(worker, task.request, task.resolve, task.reject);
  }

  /**
   * Execute a request on a specific worker
   */
  private executeOnWorker(
    instance: WorkerInstance,
    request: WorkerRequest,
    resolve: (value: any) => void,
    reject: (error: any) => void
  ) {
    instance.busy = true;
    this.pendingTasks.set(request.id, { resolve, reject });

    // Build transferables list for zero-copy transfer
    const transferables: Transferable[] = [];

    if (request.payload?.data instanceof ArrayBuffer) {
      transferables.push(request.payload.data);
    }
    if (request.payload?.vertices instanceof Float32Array) {
      transferables.push(request.payload.vertices.buffer);
    }
    if (request.payload?.positions instanceof Float32Array) {
      transferables.push(request.payload.positions.buffer);
    }
    if (request.payload?.indices instanceof Uint32Array) {
      transferables.push(request.payload.indices.buffer);
    }

    instance.worker.postMessage(request, transferables);
  }

  /**
   * Generate unique task ID
   */
  private generateTaskId(): string {
    return `task_${++this.taskIdCounter}_${Date.now()}`;
  }

  /**
   * Submit a task to the worker pool
   */
  private submitTask<T>(type: WorkerRequest['type'], payload: any): Promise<T> {
    if (!this.workersSupported) {
      return Promise.reject(new Error('Web Workers not supported'));
    }

    this.isProcessing$.next(true);

    return new Promise((resolve, reject) => {
      const request: WorkerRequest = {
        id: this.generateTaskId(),
        type,
        payload
      };

      const worker = this.getAvailableWorker();
      if (worker) {
        this.executeOnWorker(worker, request, resolve, reject);
      } else {
        // Queue the task
        this.taskQueue.push({ request, resolve, reject });
      }
    });
  }

  /**
   * Decode protobuf assembly data in a worker
   */
  async decodeProto(data: ArrayBuffer): Promise<{ parts: any[] }> {
    return this.submitTask('decode-proto', { data });
  }

  /**
   * Process PLY geometry in a worker
   */
  async processPLY(data: ArrayBuffer, cutOffAngle: number = 20): Promise<ProcessedGeometry> {
    return this.submitTask('process-ply', { data, cutOffAngle });
  }

  /**
   * Process raw geometry (vertices + indices) in a worker
   */
  async processGeometry(
    vertices: Float32Array | number[],
    indices: Uint32Array | number[],
    cutOffAngle: number = 20
  ): Promise<ProcessedGeometry> {
    return this.submitTask('process-geometry', { vertices, indices, cutOffAngle });
  }

  /**
   * Process a complete part with multiple faces
   */
  async processPart(partData: any, cutOffAngle: number = 20): Promise<ProcessedPart> {
    return this.submitTask('process-part', { partData, cutOffAngle });
  }

  /**
   * Compute BVH (Bounding Volume Hierarchy) for a geometry in a worker.
   * Returns serialized BVH data that can be deserialized on the main thread.
   *
   * Usage on main thread after receiving result:
   *   const bvh = MeshBVH.deserialize(result.serializedBVH, geometry);
   *   geometry.boundsTree = bvh;
   */
  async computeBVH(
    positions: Float32Array | number[],
    indices: Uint32Array | number[],
    options: BVHComputeOptions = {}
  ): Promise<BVHComputeResult> {
    // Clone arrays to avoid detaching geometry's buffers during transfer
    const positionsCopy = positions instanceof Float32Array
      ? new Float32Array(positions)
      : new Float32Array(positions);
    const indicesCopy = indices instanceof Uint32Array
      ? new Uint32Array(indices)
      : new Uint32Array(indices);

    return this.submitTask('compute-bvh', {
      positions: positionsCopy,
      indices: indicesCopy,
      options
    });
  }

  /**
   * Process multiple parts in parallel using the worker pool
   */
  async processPartsParallel(
    parts: any[],
    cutOffAngle: number = 20,
    onProgress?: (current: number, total: number) => void
  ): Promise<ProcessedPart[]> {
    const total = parts.length;
    let completed = 0;

    this.progress$.next({ current: 0, total });

    const promises = parts.map(async (part) => {
      const result = await this.processPart(part, cutOffAngle);
      completed++;
      this.progress$.next({ current: completed, total });
      onProgress?.(completed, total);
      return result;
    });

    return Promise.all(promises);
  }

  /**
   * Check if workers are available
   */
  get isSupported(): boolean {
    return this.workersSupported;
  }

  /**
   * Get current worker pool size
   */
  get workerCount(): number {
    return this.workers.length;
  }

  /**
   * Get number of pending tasks
   */
  get pendingCount(): number {
    return this.pendingTasks.size + this.taskQueue.length;
  }

  /**
   * Terminate all workers
   */
  terminateAll() {
    for (const instance of this.workers) {
      instance.worker.terminate();
    }
    this.workers = [];
    this.pendingTasks.clear();
    this.taskQueue = [];
    this.isProcessing$.next(false);
  }

  ngOnDestroy() {
    this.terminateAll();
  }
}
